import { useCallback, useEffect, useMemo } from 'react';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';
import { useNavigate } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';

import {
	IframePropsState,
	IsOpenInIframeState,
	PipelineSettingsFormState,
	isChecqueFraudSelected,
} from 'global-stores';
import { ROUTES } from 'routes';
import {
	ComplexOnboardingFlow,
	useComplexOnboarding,
} from 'views/complex-onboarding-flow';
import { PERMISSION_SCREEN, useUserRoles } from 'views/routes-children';
import { useGeneralSettings } from 'views/settings';
import { DeliveryMethod } from './components/delivery-method';
import { FooterNavigate } from './components/footer-navigate';
import { OnboardingHeader } from './components/header';
import { OnboardingLeftNav } from './components/left-nav';
import { Publish } from './components/publish';
import { useSv2Theme } from 'views/design-system';
import 'views/design-system/tokens.scss';
import 'views/design-system/surface.scss';
import './onboarding-flow.scss';
import './builder-panel-ledger.scss';
import {
	AddedActionsState,
	OnboardingFooterNavigateState,
	SelectedStepsState,
	SettingFormInitState,
} from './store';
import classNames from 'classnames';
import { SessionOutModal } from 'views/session-out';
import { useOnboardingLock } from 'hooks';

const OnboardingFlow = () => {
	// global states
	const navigate = useRecoilValue(OnboardingFooterNavigateState);
	const selectedSteps = useRecoilValue(SelectedStepsState);
	const [addedActions, setAddedActions] = useRecoilState(AddedActionsState);
	const { background } = useRecoilValue(IframePropsState);
	const isIframeOpen = useRecoilValue(IsOpenInIframeState);
	const [settingsForm, setSettingsForm] = useRecoilState(
		PipelineSettingsFormState
	);
	const settingFormInit = useRecoilValue(SettingFormInitState);
	const isChequeFraud = useRecoilValue(isChecqueFraudSelected);
	// hooks
	const { getClientDetails } = useGeneralSettings();
	// Hold the theme claim so walking from a dark Sessions page into the builder
	// does not drop the shell back to light mid-journey.
	useSv2Theme();

	const { checkUserWritePermission } = useUserRoles();
	const navigator = useNavigate();
	const { handleContinueActivity, handleLeave, countdown, inactiveModalOpen } =
		useOnboardingLock();

	const isUserPermissionWrite = useMemo(
		() => checkUserWritePermission(PERMISSION_SCREEN.Onboarding),
		[checkUserWritePermission]
	);

	useEffect(() => {
		if (!isUserPermissionWrite) navigator(ROUTES.PIPELINE);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isUserPermissionWrite]);

	//use effect
	useEffect(() => {
		getClientDetails();
		// eslint-disable-next-line
	}, []);

	useEffect(() => {
		/*@avinash: as this is not required for modify or clone
		 */
		if (!settingsForm.isModifyOrClone) {
			setSettingsForm(settingFormInit);
		}

		//@avinash: removing isModifyOrClone key from settting form object when component will be unmont
		return () => {
			setSettingsForm(prev => {
				const newObj = structuredClone(prev);
				delete newObj.isModifyOrClone;
				return newObj;
			});
		};
		// eslint-disable-next-line
	}, []);

	// local variables
	const globalActions = useMemo(() => {
		const stepActions: any[] = [];
		selectedSteps.forEach(({ actions }) => {
			stepActions.push(...actions);
		});
		return stepActions;
	}, [selectedSteps]);

	const leftNavActions = useMemo(() => {
		if (navigate === 'complex-flow') {
			return globalActions;
		}
		return globalActions.filter(
			action =>
				!addedActions.find(addedAction => addedAction._id === action._id)
		);
	}, [addedActions, globalActions, navigate]);

	// lagacy code do not change
	const onDragEnd = useCallback(
		(result: DropResult) => {
			/* 
			  @avinashSatschel
              If the 'isChequeFraud' flag is set to true, prevent further execution of the code block.
              This condition ensures that additional actions are not added when Cheque Fraud is enabled,
              as Cheque Fraud handling may require a specific flow without additional steps.
             */
			if (isChequeFraud) {
				// Exit the function to prevent further processing when Cheque Fraud is enabled.
				return;
			}
			// destination and source extracted here
			const { draggableId: actionKey } = result;
			const found = leftNavActions.find(item => item.key === actionKey);
			if (found) {
				setAddedActions(prev => {
					const prevObj = JSON.parse(JSON.stringify(prev));
					prevObj.push({ ...found, nodeId: (result as any).nodeId });
					return prevObj;
				});
			}
			return;
		},
		[isChequeFraud, leftNavActions, setAddedActions]
	);

	// this will render different views / screens in creating new onboarding
	const renderView = useCallback(() => {
		switch (navigate) {
			case 'complex-flow':
				return <ComplexOnboardingFlow handleOnDrop={onDragEnd} />;
			case 'deliveryMethod':
				return <DeliveryMethod />;
			case 'publish':
				return <Publish />;
			default:
				return <ComplexOnboardingFlow handleOnDrop={onDragEnd} />;
		}
	}, [navigate, onDragEnd]);

	const getHeader = useMemo(() => <OnboardingHeader />, []);

	const { onDragStart } = useComplexOnboarding();

	const onboardingleftFlowClass = classNames('OnboardingFlow--left', {
		'OnboardingFlow--left--config':
			navigate !== 'selectSteps' && navigate !== 'complex-flow',
	});

	const renderComponent = useMemo(() => {
		return (
			<div
				className="OnboardingFlow--container PanelLedger"
				style={{ backgroundColor: background }}
			>
				{getHeader}

				<div
					className="OnboardingFlow--contain"
					style={{
						...(isIframeOpen && {
							marginTop: 0,
							height: '100%',
						}),
					}}
				>
					<div className={onboardingleftFlowClass}>
						<OnboardingLeftNav navigate={navigate} onDragStart={onDragStart} />
					</div>

					{/* The rail's width comes from .OnboardingFlow--left; `flex: 1`
						already gives this column the rest, and the hardcoded
						`calc(100% - 430px)` only kept it in step with the OLD rail. */}
					<div className="OnboardingFlow--right">{renderView()}</div>
				</div>

				{/* The footer bar spans the full width, under the rail as well as the
					canvas, the way the concept's <footer> is a sibling of .body -- not
					a strip that starts where the canvas starts. */}
				<FooterNavigate />
			</div>
		);
	}, [
		navigate,
		background,
		getHeader,
		isIframeOpen,
		onboardingleftFlowClass,
		onDragStart,
		renderView,
	]);

	if (inactiveModalOpen) {
		return (
			<SessionOutModal
				isOpen={inactiveModalOpen}
				closeModal={() => handleContinueActivity()}
				primaryBtnConfig={{
					label: 'Stay',
					handleClick: handleContinueActivity,
				}}
				secondaryBtnConfig={{
					label: 'Leave',
					handleClick: handleLeave,
				}}
				subHeading={
					<>
						It looks like you’ve been inactive for a while. Your onboarding session will end in{' '}
						<b className='OnboardingFlow--countdown'>
							{Math.floor(countdown / 60)}:
							{(countdown % 60).toString().padStart(2, '0')}
						</b>{' '}
						minutes. You’ll be redirected to the home page unless you resume
						activity.{' '}
					</>
				}
			/>
		);
	}

	return (
		<DragDropContext onDragEnd={onDragEnd}>{renderComponent}</DragDropContext>
	);
};

export default OnboardingFlow;
